import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles, RotateCcw, ChevronRight } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { lessonChat } from '../services/api';
import { trackEvent } from '../utils/telemetry';
import { ROLE_STARTER_PROMPTS, ROLE_LABELS, getRoleWelcomeMessage } from '../utils/roleUtils';

/**
 * GlobalAssistantDrawer — lightweight slide-in panel accessible from the header.
 *
 * General Saige assistant (not lesson-chat clone). Supports:
 *   - Starter prompts for common planning needs
 *   - Free-form question input
 *   - Context-awareness via userProfile
 *
 * Preserves LessonChatPanel for in-lesson PostGenerationView.
 */

// Starter prompts are role-aware — resolved per render from roleUtils

const GlobalAssistantDrawer = ({ isOpen, onClose, userProfile, selectedSubject, gradeLevel, userRole = 'teacher' }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => { scrollToBottom(); }, [messages]);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([{
                role: 'assistant',
                content: getRoleWelcomeMessage(userRole, userProfile?.name),
            }]);
        }
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const buildAssistantContext = () => ({
        subject: selectedSubject || userProfile?.subject || null,
        grade_level: gradeLevel || userProfile?.grade_level || null,
        unit_name: 'General Saige Assistant',
        objectives: [],
        lesson_flow: [],
        materials: [],
        assistant_mode: 'general_assistant',
        teacher_name: userProfile?.name || null,
        school: userProfile?.school || null,
        district: userProfile?.district || null,
    });

    const buildConversationHistory = () => (
        messages
            .filter((message) => message.content && message.role !== 'system')
            .slice(1)
            .map((message) => ({
                role: message.role === 'user' ? 'user' : 'ai',
                text: message.content,
            }))
    );

    const resolveErrorMessage = (responseData, httpErr) => {
        const code = responseData?.error_code;
        if (code === 'AUTH_REQUIRED') {
            return 'Your session has expired. Please refresh and sign in again.';
        }
        if (code === 'FEATURE_DISABLED') {
            return 'Saige Assistant is not available for this account yet.';
        }
        if (code === 'QUOTA_EXCEEDED') {
            return "You've reached the assistant limit for this session.";
        }
        if (code === 'PROVIDER_ERROR') {
            return 'Saige is having trouble right now. Please try again in a moment.';
        }
        if (code === 'INVALID_PAYLOAD' || httpErr?.response?.status === 422) {
            return 'Saige could not understand that request. Please try again in a moment.';
        }
        if (httpErr) {
            return 'Could not get a response. Please try again in a moment.';
        }
        return null;
    };

    const sendMessage = async (messageText) => {
        const text = messageText || input.trim();
        if (!text || loading) return;
        setInput('');
        setError(null);

        const userMessage = { role: 'user', content: text };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setLoading(true);

        trackEvent('global_assistant_message_sent', { prompt_length: text.length });

        try {
            const response = await lessonChat.send({
                teacher_email: userProfile?.email,
                message: text,
                lesson_id: null,
                lesson_context: buildAssistantContext(),
                history: buildConversationHistory(),
            });

            const typedError = resolveErrorMessage(response.data, null);
            if (response.data?.error_code) {
                setError(typedError || 'Could not get a response. Please try again in a moment.');
                return;
            }

            const reply = response.data?.response || response.data?.reply || response.data?.message || "I'm here to help — could you rephrase that?";
            setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
        } catch (err) {
            console.error('Global assistant error:', err);
            setError(resolveErrorMessage(err.response?.data, err));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setMessages([]);
        setError(null);
        setInput('');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
                        onClick={onClose}
                    />

                    {/* Drawer Panel */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '100%', opacity: 0 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                        className="fixed right-0 top-0 h-full w-full max-w-[400px] bg-white shadow-[0_0_40px_rgba(0,0,0,0.15)] z-50 flex flex-col"
                        style={{ borderLeft: '1px solid rgba(0,0,0,0.06)' }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 flex-shrink-0">
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
                                    <Sparkles size={14} className="text-black" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-semibold text-[#1d1d1f]">Saige Assistant</h2>
                                    <p className="text-[10px] text-[#999]">
                                        {userRole !== 'teacher'
                                            ? ROLE_LABELS[userRole] || 'General help'
                                            : selectedSubject && gradeLevel
                                                ? `${selectedSubject} · ${gradeLevel}`
                                                : 'General help'
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {messages.length > 1 && (
                                    <button
                                        onClick={handleReset}
                                        className="p-1.5 text-[#bbb] hover:text-[#666] transition-colors rounded-lg hover:bg-slate-100"
                                        title="Start over"
                                    >
                                        <RotateCcw size={14} />
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="p-1.5 text-[#bbb] hover:text-[#666] transition-colors rounded-lg hover:bg-slate-100"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                            {messages.map((msg, idx) => (
                                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                                        msg.role === 'user'
                                            ? 'bg-[#FFC107] text-black rounded-br-sm font-medium'
                                            : 'bg-[#f5f5f7] text-[#1d1d1f] rounded-bl-sm'
                                    }`}>
                                        <p className="whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                </div>
                            ))}

                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-[#f5f5f7] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin text-[#999]" />
                                        <span className="text-sm text-[#999]">Thinking…</span>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2">
                                    {error}
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Starter prompts — role-aware, only when no conversation yet */}
                        {messages.length <= 1 && !loading && (() => {
                            const prompts = ROLE_STARTER_PROMPTS[userRole] || ROLE_STARTER_PROMPTS.teacher;
                            return (
                                <div className="px-4 pb-3 flex-shrink-0">
                                    <p className="text-[10px] text-[#bbb] uppercase tracking-wide font-medium mb-2">Quick starters</p>
                                    <div className="space-y-1.5">
                                        {prompts.map((prompt, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => sendMessage(prompt.prompt)}
                                                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm text-[#444] bg-[#fafafa] hover:bg-amber-50 hover:text-amber-700 border border-black/5 hover:border-amber-200 transition-all duration-150"
                                            >
                                                <span className="text-base">{prompt.icon}</span>
                                                <span className="flex-1 font-medium text-xs">{prompt.label}</span>
                                                <ChevronRight size={12} className="text-[#bbb] flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Input */}
                        <div className="px-4 pb-5 pt-3 border-t border-black/5 flex-shrink-0">
                            <div className="flex items-end gap-2">
                                <textarea
                                    ref={inputRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                    placeholder="Ask anything…"
                                    rows={1}
                                    className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-black/10 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none bg-[#fafafa] text-[#1d1d1f] placeholder-[#bbb]"
                                    style={{ minHeight: '40px', maxHeight: '120px', overflowY: 'auto' }}
                                />
                                <button
                                    onClick={() => sendMessage()}
                                    disabled={!input.trim() || loading}
                                    className="w-9 h-9 flex items-center justify-center bg-[#FFC107] text-black rounded-xl hover:bg-[#e6ad00] disabled:opacity-40 transition-all flex-shrink-0"
                                >
                                    {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                </button>
                            </div>
                            <p className="text-[10px] text-[#ccc] mt-2 text-center">Shift+Enter for new line · Enter to send</p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default GlobalAssistantDrawer;
